// 생산도구 > 배송요약 — EBMS 납품일지2를 고른 기간만큼 바로 가져와 거래처별 · 제품타입별 출고 수량을 본다.
// 제품타입은 데이터 포털 제품 분류(1+2+3단계 이름)와 맞춘 결과. 수량 = 조작 좌+우 (단위 구분 전).
import React, { useMemo, useState } from 'react';
import {
  ConfigProvider, DatePicker, Button, Space, Table, Tag, Card, Statistic, Row, Col, Typography, Alert, Tabs, Input,
  Segmented, message, Grid,
} from 'antd';
// 배송구분: 당일(출고일 D) 화물·택배·화물2·택배2 / 다음날(D+1) 픽업·시공·납품·픽업2 — 서버가 작업일 D 기준으로 묶어 준다
import { SearchOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import ko_KR from 'antd/es/locale/ko_KR';
import ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import { API_BASE } from '../config/api';
import '../styles/Common.css';

dayjs.locale('ko');

const { RangePicker } = DatePicker;
const { Text } = Typography;

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const MAX_DAYS = 31;
const UNSET_KEY = '__UNSET';
const RESULT_META = {
  TYPE: { color: 'green', label: '타입' },
  UNSET: { color: 'gold', label: '미지정' },
  UNMATCHED: { color: 'red', label: '미매칭' },
  AMBIGUOUS: { color: 'purple', label: '모호' },
  NO_PORTAL: { color: 'default', label: '포털 없음' },
  SKIP: { color: 'default', label: '대상 아님' },
};
const SRC_LABEL = { GOODS: '품목', CLS: '분류' }; // 기초품목(제품명+원단코드) / 제품 분류
const TYPE_COLORS = ['blue', 'cyan', 'geekblue', 'purple', 'magenta', 'volcano', 'orange', 'lime', 'green', 'gold'];

const fmtQty = (v) => (v == null ? '-' : Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 3 }));
// 출고메모 줄의 비고 내용 (날짜가 여러 날이면 날짜 표시)
const MemoList = ({ memos, multiDay }) => (memos?.length ? (
  <ul className="ds-memos">
    {memos.map((m, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <li key={i}>{multiDay && <Text type="secondary">{dayjs(m.date).format('M/D')} </Text>}{m.note}{m.qty > 1 && <Text type="secondary"> ×{fmtQty(m.qty)}</Text>}</li>
    ))}
  </ul>
) : null);

const GROUP_LABEL = { SAME: '당일', NEXT: '다음날', OTHER: '기타' };
const GROUP_COLOR = { SAME: 'blue', NEXT: 'green', OTHER: 'default' };
// A4에서 원단코드를 한 줄씩 적는 제품타입 (코드 또는 이름)
const DETAIL_TYPES = new Set(['MOACC', '전동악세사리', 'ACC', '악세사리']);
const ShipTag = ({ ship, group }) => <Tag color={GROUP_COLOR[group] || 'default'} style={{ marginInlineEnd: 4 }}>{ship}</Tag>;

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// 출고메모 줄은 메모 표시용 — 수량·제품타입·미지정 집계에서 뺀다
const isMemo = (it) => it.item_code === '출고메모';
// 단가단위가 대상이 아닌 품목 (서버가 보통 빼고 보내지만 남아 있으면 표시만 하고 집계에서 뺀다)
const isSkip = (it) => it.match?.result === 'SKIP';
const notCounted = (it) => isMemo(it) || isSkip(it);
// 한샘_NEW·코스트코는 주문번호, 한샘몰은 이름+전화번호로 서버가 나눠 보낸다 (customer_sub)
// 미터 단위 전동악세사리: 수량은 개수, 길이는 따로
const Lengths = ({ r }) => (r.lengths_m?.length ? <Text type="secondary"> ({r.lengths_m.map((m) => `${fmtQty(m)}m`).join(', ')})</Text> : null);
const SubName = ({ sub }) => (sub ? <Tag style={{ marginInlineStart: 6 }}>{sub}</Tag> : null);

const typeKey = (it) => (it.match?.result === 'TYPE' ? it.match.type_cd : UNSET_KEY);

const DeliverySummaryPage = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === undefined ? window.innerWidth < 768 : !screens.md;
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [resultFilter, setResultFilter] = useState('ALL');
  const [shipSel, setShipSel] = useState([]); // 고른 배송구분
  const [splitShip, setSplitShip] = useState(false); // 거래처별을 배송구분으로 나눠 보기

  const fetchData = async () => {
    if (!range?.[0] || !range?.[1]) { message.warning('기간을 고르세요.'); return; }
    if (range[1].diff(range[0], 'day') + 1 > MAX_DAYS) { message.warning(`한 번에 ${MAX_DAYS}일까지 조회할 수 있습니다.`); return; }
    setLoading(true);
    try {
      const { data: res } = await api.get('/api/delivery-summary', {
        params: { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') },
        timeout: 180000,
      });
      setData(res);
      // 기본: 당일·다음날 구분 전부 (그 밖의 구분은 해제)
      setShipSel((res.ships || []).map((x) => x.ship)); // 검색된 배송구분은 모두 체크
    } catch (e) {
      message.error(e.response?.data?.message || 'EBMS 출고 자료를 가져오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 화면에 쓰는 제품타입 열: 포털 타입 중 이번 결과에 나온 것 + 미지정(타입 없음)
  const typeCols = useMemo(() => {
    if (!data) return [];
    const present = new Set(data.items.filter((it) => !notCounted(it)).map(typeKey));
    const cols = data.types.filter((t) => present.has(t.code)).map((t, i) => ({ ...t, color: TYPE_COLORS[i % TYPE_COLORS.length] }));
    // 포털 타입 목록에 없는 코드(새로 생긴 타입 등)도 빠뜨리지 않는다
    const known = new Set(cols.map((c) => c.code));
    data.items.forEach((it) => {
      if (notCounted(it)) return;
      const k = typeKey(it);
      if (k !== UNSET_KEY && !known.has(k)) { known.add(k); cols.push({ code: k, name: it.match.type_nm || k, color: 'default' }); }
    });
    if (present.has(UNSET_KEY)) cols.push({ code: UNSET_KEY, name: '미지정·미매칭', color: 'default' });
    return cols;
  }, [data]);
  const typeColor = useMemo(() => Object.fromEntries(typeCols.map((t) => [t.code, t.color])), [typeCols]);

  const items = useMemo(() => {
    if (!data) return [];
    const kw = q.trim().toLowerCase();
    const sel = new Set(shipSel);
    return data.items.filter((it) => sel.has(it.ship) && (!kw || `${it.customer} ${it.product}`.toLowerCase().includes(kw)));
  }, [data, q, shipSel]);

  // 거래처별 (행) × 제품타입 (열)
  const customers = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = `${it.customer}\u0000${it.customer_kind}\u0000${it.customer_sub || ''}${splitShip ? `\u0000${it.ship}` : ''}`;
      if (!map.has(key)) {
        map.set(key, {
          key, customer: it.customer, customer_kind: it.customer_kind, customer_sub: it.customer_sub || '', ...(splitShip ? { ship: it.ship, ship_group: it.ship_group } : {}),
          total: 0, byType: {}, products: [], bracketSet: new Set(), pieceSet: new Set(), shipSet: new Map(),
        });
      }
      const c = map.get(key);
      c.shipSet.set(it.ship, it.ship_group);
      (it.bracket_orders || []).forEach((o) => c.bracketSet.add(o));
      (it.piece_orders || []).forEach((o) => c.pieceSet.add(o));
      const k = typeKey(it);
      if (!notCounted(it)) {
        c.total += it.qty;
        c.byType[k] = (c.byType[k] || 0) + it.qty;
      }
      c.products.push(it);
    });
    return [...map.values()].map(({ bracketSet, pieceSet, shipSet, ...c }) => ({
      ...c,
      bracket: bracketSet.size,
      piece: pieceSet.size,
      ships: [...shipSet].map(([ship, group]) => ({ ship, group })),
      products: c.products.sort((a, b) => (isMemo(b) - isMemo(a)) || b.qty - a.qty), // 출고메모 먼저
    })).sort((a, b) => b.total - a.total);
  }, [items, splitShip]);

  // 제품타입별 → 제품별
  const byType = useMemo(() => typeCols.map((t) => {
    const its = items.filter((it) => !notCounted(it) && typeKey(it) === t.code);
    const prod = new Map();
    its.forEach((it) => {
      if (!prod.has(it.product_base)) prod.set(it.product_base, { key: it.product_base, product_base: it.product_base, match: it.match, qty: 0, customers: new Set() });
      const p = prod.get(it.product_base);
      p.qty += it.qty;
      p.customers.add(it.customer);
    });
    return {
      key: t.code,
      name: t.name,
      color: t.color,
      qty: its.reduce((a, it) => a + it.qty, 0),
      customers: new Set(its.map((it) => it.customer)).size,
      products: [...prod.values()].map((p) => ({ ...p, customers: p.customers.size })).sort((a, b) => b.qty - a.qty),
    };
  }).filter((t) => t.qty || t.products.length), [items, typeCols]);

  // 미지정 · 미매칭 제품 (포털 제품 분류 화면에 넘길 목록)
  const unresolved = useMemo(() => {
    const map = new Map();
    items.filter((it) => !notCounted(it) && it.match?.result !== 'TYPE').forEach((it) => {
      const key = `${it.product_base}\u0000${it.item_code}`;
      if (!map.has(key)) map.set(key, { key, product_base: it.product_base, item_code: it.item_code, match: it.match, qty: 0, lines: 0 });
      const p = map.get(key);
      p.qty += it.qty;
      p.lines += it.lines;
    });
    return [...map.values()].sort((a, b) => b.qty - a.qty);
  }, [items]);

  const multiDay = !!data && data.from !== data.to;
  // 거래처에 출고메모가 있으면 표시 (펼치면 내용)
  // 비고에 ㄱ → 브라켓, ㅍ → 피스 (주문 건수)
  const partTags = (bracket, piece) => (
    <>
      {bracket > 0 && <Tag color="geekblue" style={{ marginLeft: 6 }}>브라켓 {bracket}</Tag>}
      {piece > 0 && <Tag color="cyan" style={{ marginLeft: 6 }}>피스 {piece}</Tag>}
    </>
  );
  const memoTag = (c) => (c.products.some((p) => p.memos?.length) ? <Tag color="volcano" style={{ marginLeft: 6 }}>출고메모</Tag> : null);
  const totalQty = items.reduce((a, it) => a + (notCounted(it) ? 0 : it.qty), 0);
  const ebmsMismatch = data?.days.filter((d) => d.ebms_total != null && Math.abs(d.ebms_total - d.qty) > 0.01) || [];

  const matchTag = (m) => {
    if (!m) return null;
    const src = SRC_LABEL[m.src] ? <Text type="secondary" style={{ fontSize: 11, marginInlineEnd: 6 }}>{SRC_LABEL[m.src]}</Text> : null;
    if (m.result === 'TYPE') return <><Tag color={typeColor[m.type_cd] || 'blue'}>{m.type_nm}</Tag>{src}</>;
    const meta = RESULT_META[m.result] || RESULT_META.UNMATCHED;
    return <><Tag color={meta.color}>{meta.label}{m.result === 'SKIP' && m.unit ? ` (${m.unit})` : ''}</Tag>{src}</>;
  };

  // 폰: 제품명 아래에 타입 태그를 붙여 두 칸(제품·수량)만
  const productColumns = isMobile ? [
    {
      title: '제품명 · 수량', key: 'm', align: 'left',
      render: (_, r) => (
        <div className="ds-m-row">
          <div style={{ wordBreak: 'break-all', flex: 1, minWidth: 0 }}><ShipTag ship={r.ship} group={r.ship_group} />{r.product}<Lengths r={r} /><MemoList memos={r.memos} multiDay={multiDay} /><div style={{ marginTop: 2 }}>{isMemo(r) ? <Tag color="volcano">출고메모</Tag> : matchTag(r.match)}{partTags(r.bracket_orders?.length, r.piece_orders?.length)}</div></div>
          <b>{isMemo(r) ? '' : isSkip(r) ? <Text type="secondary">{fmtQty(r.qty)}</Text> : fmtQty(r.qty)}</b>
        </div>
      ),
    },
  ] : [
    {
      title: '제품명', dataIndex: 'product', key: 'product', align: 'left',
      render: (v, r) => (
        <div style={{ wordBreak: 'break-all' }}>
          <ShipTag ship={r.ship} group={r.ship_group} />
          {v}
          <Lengths r={r} />
          {partTags(r.bracket_orders?.length, r.piece_orders?.length)}
          <MemoList memos={r.memos} multiDay={multiDay} />
        </div>
      ),
    },
    ...(isMobile ? [] : [{ title: '제품타입', key: 'type', width: 110, render: (_, r) => (isMemo(r) ? <Tag color="volcano">출고메모</Tag> : matchTag(r.match)) }]),
    { title: '수량', dataIndex: 'qty', key: 'qty', width: 90, align: 'right', render: (v, r) => (isMemo(r) ? '' : isSkip(r) ? <Text type="secondary">{fmtQty(v)}</Text> : fmtQty(v)), sorter: (a, b) => a.qty - b.qty },
    { title: '건', dataIndex: 'orders', key: 'orders', width: 60, align: 'right', responsive: ['md'] },
  ];

  const customerColumns = isMobile ? [
    {
      title: '거래처 · 합계', key: 'm', align: 'left',
      render: (_, r) => (
        <div className="ds-m-row">
          <span style={{ flex: 1, minWidth: 0 }}><Text strong>{r.customer}</Text>{r.customer_kind && <Text type="secondary" style={{ fontSize: 12 }}> · {r.customer_kind}</Text>}<SubName sub={r.customer_sub} />{memoTag(r)}{partTags(r.bracket, r.piece)}<div style={{ marginTop: 2 }}>{r.ships.map((x) => <ShipTag key={x.ship} ship={x.ship} group={x.group} />)}</div></span>
          <b>{fmtQty(r.total)}</b>
        </div>
      ),
    },
  ] : [
    {
      title: '거래처', dataIndex: 'customer', key: 'customer', fixed: isMobile ? undefined : 'left', width: isMobile ? undefined : 240, align: 'left',
      render: (v, r) => <span><Text strong>{v}</Text>{r.customer_kind && <Text type="secondary" style={{ fontSize: 12 }}> · {r.customer_kind}</Text>}<SubName sub={r.customer_sub} />{memoTag(r)}</span>,
      sorter: (a, b) => a.customer.localeCompare(b.customer) || a.customer_sub.localeCompare(b.customer_sub),
    },
    {
      title: '배송구분', key: 'ships', width: splitShip ? 90 : 160, align: 'left',
      render: (_, r) => r.ships.map((x) => <ShipTag key={x.ship} ship={x.ship} group={x.group} />),
      ...(splitShip ? { sorter: (a, b) => a.ship.localeCompare(b.ship) } : {}),
    },
    {
      title: '브라켓', dataIndex: 'bracket', key: 'bracket', width: 76, align: 'right',
      render: (v) => (v ? <Tag color="geekblue" style={{ margin: 0 }}>{v}건</Tag> : <Text type="secondary">-</Text>),
      sorter: (a, b) => a.bracket - b.bracket,
    },
    {
      title: '피스', dataIndex: 'piece', key: 'piece', width: 70, align: 'right',
      render: (v) => (v ? <Tag color="cyan" style={{ margin: 0 }}>{v}건</Tag> : <Text type="secondary">-</Text>),
      sorter: (a, b) => a.piece - b.piece,
    },
    {
      title: '합계', dataIndex: 'total', key: 'total', width: 100, align: 'right',
      render: (v) => <Text strong>{fmtQty(v)}</Text>, sorter: (a, b) => a.total - b.total, defaultSortOrder: 'descend',
    },
    ...(isMobile ? [] : typeCols.map((t) => ({
      title: t.name, key: `t_${t.code}`, width: 90, align: 'right',
      render: (_, r) => (r.byType[t.code] ? fmtQty(r.byType[t.code]) : <Text type="secondary">-</Text>),
      sorter: (a, b) => (a.byType[t.code] || 0) - (b.byType[t.code] || 0),
    }))),
  ];

  const typeColumns = [
    { title: '제품타입', dataIndex: 'name', key: 'name', align: 'left', render: (v, r) => <Tag color={r.color}>{v}</Tag> },
    { title: '수량', dataIndex: 'qty', key: 'qty', width: 110, align: 'right', render: (v) => <Text strong>{fmtQty(v)}</Text>, sorter: (a, b) => a.qty - b.qty, defaultSortOrder: 'descend' },
    { title: '거래처 수', dataIndex: 'customers', key: 'customers', width: 100, align: 'right' },
    { title: '제품 수', key: 'pc', width: 90, align: 'right', render: (_, r) => r.products.length },
  ];

  const typeProductColumns = [
    { title: '제품 (분류 이름)', dataIndex: 'product_base', key: 'p', align: 'left', render: (v) => <span style={{ wordBreak: 'break-all' }}>{v}</span> },
    { title: 'EBMS 코드', key: 'cd', width: 100, responsive: ['md'], render: (_, r) => r.match?.ebms_cd || '-' },
    { title: '수량', dataIndex: 'qty', key: 'qty', width: 100, align: 'right', render: fmtQty },
    { title: '거래처', dataIndex: 'customers', key: 'c', width: 80, align: 'right' },
  ];

  const unresolvedColumns = [
    { title: '결과', key: 'r', width: 90, render: (_, r) => matchTag(r.match) },
    { title: '제품명', dataIndex: 'product_base', key: 'p', align: 'left', render: (v) => <div style={{ wordBreak: 'break-all' }}>{v}</div> },
    { title: '원단코드', dataIndex: 'item_code', key: 'f', align: 'left', render: (v) => <div style={{ wordBreak: 'break-all' }}>{v || '-'}</div> },
    { title: 'EBMS 코드', key: 'cd', width: 100, render: (_, r) => r.match?.ebms_cd || '-' },
    { title: '건수', dataIndex: 'lines', key: 'n', width: 70, align: 'right' },
    { title: '수량', dataIndex: 'qty', key: 'qty', width: 90, align: 'right', render: fmtQty },
  ];

  const handleExcel = async () => {
    if (!data) return;
    const wb = new ExcelJS.Workbook();
    const ws1 = wb.addWorksheet('거래처별');
    ws1.addRow([`배송요약 ${data.from} ~ ${data.to} (수량 = 조작 좌+우) · 배송구분: ${shipSel.join(', ')}`]);
    ws1.addRow([]);
    const h1 = ws1.addRow(['거래처', '구분', '주문·수령인', '배송구분', '합계', '브라켓(건)', '피스(건)', ...typeCols.map((t) => t.name)]);
    h1.font = { bold: true };
    customers.forEach((c) => ws1.addRow([c.customer, c.customer_kind, c.customer_sub, c.ships.map((x) => x.ship).join(', '), c.total, c.bracket, c.piece, ...typeCols.map((t) => c.byType[t.code] || 0)]));
    ws1.columns.forEach((col, i) => { col.width = i === 0 ? 34 : i === 2 ? 26 : 12; });

    const ws2 = wb.addWorksheet('상세');
    const h2 = ws2.addRow(['배송구분', '거래처', '구분', '주문·수령인', '제품명', '분류 이름', '원단코드', '제품타입', '매칭', '출처', 'EBMS 코드', '수량', '건', '출고일', '출고메모']);
    h2.font = { bold: true };
    items.forEach((it) => ws2.addRow([
      it.ship, it.customer, it.customer_kind, it.customer_sub || '', it.product, it.product_base, it.item_code,
      it.match?.type_nm || '', RESULT_META[it.match?.result]?.label || '', SRC_LABEL[it.match?.src] || '', it.match?.ebms_cd || '',
      it.qty, it.orders, it.dates.join(', '),
      (it.memos || []).map((m) => (multiDay ? `${m.date.slice(5)} ${m.note}` : m.note)).join(' / '),
    ]));
    ws2.columns.forEach((col, i) => { col.width = [8, 30, 8, 24, 50, 40, 24, 10, 8, 6, 10, 10, 6, 24, 40][i]; });

    const ws3 = wb.addWorksheet('미지정·미매칭');
    const h3 = ws3.addRow(['결과', '출처', '제품명', '원단코드', 'EBMS 코드', '건수', '수량']);
    h3.font = { bold: true };
    unresolved.forEach((p) => ws3.addRow([RESULT_META[p.match?.result]?.label || '', SRC_LABEL[p.match?.src] || '', p.product_base, p.item_code, p.match?.ebms_cd || '', p.lines, p.qty]));
    ws3.columns.forEach((col, i) => { col.width = [8, 6, 44, 30, 10, 8, 10][i]; });

    const buf = await wb.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buf]), `배송요약_${data.from}_${data.to}.xlsx`);
  };

  // A4 출력 — 배송구분별로 나눈 뒤 거래처마다 한 줄 요약 (제품 상세 없이). 고른 배송구분·검색 조건 그대로.
  const handlePrint = () => {
    if (!data || !items.length) { message.info('인쇄할 내용이 없습니다.'); return; }
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) { message.warning('팝업이 차단되었습니다. 팝업을 허용해 주세요.'); return; }
    const shipOrder = (data.ships || []).map((x) => x.ship);
    const groupOf = Object.fromEntries((data.ships || []).map((x) => [x.ship, x.group]));
    const byShip = new Map();
    items.forEach((it) => {
      if (!byShip.has(it.ship)) byShip.set(it.ship, new Map());
      const custs = byShip.get(it.ship);
      const key = `${it.customer}\u0000${it.customer_kind}\u0000${it.customer_sub || ''}`;
      if (!custs.has(key)) custs.set(key, { seq: it.seq ?? Infinity, customer: it.customer, kind: it.customer_kind, sub: it.customer_sub || '', subKey: it.customer_sub_key || '', qty: 0, types: {}, accs: new Map(), bracket: new Set(), piece: new Set(), memos: [] });
      const c = custs.get(key);
      if ((it.seq ?? Infinity) < c.seq) c.seq = it.seq;
      if (!notCounted(it)) {
        c.qty += it.qty;
        const tn = it.match?.result === 'TYPE' ? it.match.type_nm : '기타';
        c.types[tn] = (c.types[tn] || 0) + it.qty;
        // 전동악세사리·악세사리는 원단코드(리모컨·스위치·부속 종류)를 코드마다 한 줄씩 적는다
        if (DETAIL_TYPES.has(it.match?.type_cd) || DETAIL_TYPES.has(it.match?.type_nm)) {
          const code = it.item_code || it.product_base;
          const a = c.accs.get(code) || { qty: 0, lengths: [] };
          a.qty += it.qty;
          a.lengths.push(...(it.lengths_m || []));
          c.accs.set(code, a);
        }
      }
      (it.bracket_orders || []).forEach((o) => c.bracket.add(o));
      (it.piece_orders || []).forEach((o) => c.piece.add(o));
      (it.memos || []).forEach((m) => { if (!c.memos.includes(m.note)) c.memos.push(m.note); }); // 같은 메모는 한 번만
    });
    const dayOf = (ship) => {
      if (multiDay) return groupOf[ship] === 'NEXT' ? '출고일 D+1' : '출고일 D';
      return (groupOf[ship] === 'NEXT' ? dayjs(data.from).add(1, 'day') : dayjs(data.from)).format('M/D(dd)');
    };
    const sections = [...byShip.keys()].sort((a, b) => shipOrder.indexOf(a) - shipOrder.indexOf(b)).map((ship) => {
      const custs = [...byShip.get(ship).values()].sort((a, b) => a.seq - b.seq); // EBMS 크롤링 순서 그대로
      const qty = custs.reduce((a, c) => a + c.qty, 0);
      const rows = custs.map((c, i) => `<tr>
        <td class="no">${i + 1}</td>
        <td>${c.subKey
    // 한샘 나눈 줄: '한샘) 주문번호' 또는 '한샘) 주문자' (나머지 정보는 아래 작게)
    ? `<b>한샘) ${esc(c.subKey)}</b>${c.sub !== c.subKey ? `<div class="sub">${esc(c.sub.replace(c.subKey, '').trim())}</div>` : ''}`
    : `<b>${esc(c.customer)}</b>${c.kind ? ` <small>${esc(c.kind)}</small>` : ''}${c.sub ? `<div class="sub">${esc(c.sub)}</div>` : ''}`}</td>
        <td class="types">${Object.entries(c.types).sort((a, b) => b[1] - a[1]).map(([t, v]) => `${esc(t)} ${fmtQty(v)}`).join(' · ')}${[...c.accs].map(([code, a]) => `<div class="acc">${esc(code)}${a.lengths.length ? ` ${a.lengths.map((m) => `${fmtQty(m)}m`).join(', ')}` : ''} × ${fmtQty(a.qty)}</div>`).join('')}</td>
        <td class="num"><b>${c.qty ? fmtQty(c.qty) : ''}</b></td>
        <td class="num">${c.bracket.size || ''}</td>
        <td class="num">${c.piece.size || ''}</td>
        <td class="memo">${c.memos.map((m) => `<div>${esc(m)}</div>`).join('')}</td>
      </tr>`).join('');
      return `<section>
        <h2>${esc(ship)} <span>${esc(GROUP_LABEL[groupOf[ship]] || '')} ${esc(dayOf(ship))} · 거래처 ${custs.length}곳 · 수량 ${fmtQty(qty)}</span></h2>
        <table>
          <colgroup><col style="width:5%"><col style="width:30%"><col><col style="width:9%"><col style="width:6%"><col style="width:6%"><col style="width:20%"></colgroup>
          <thead><tr><th>No</th><th>거래처</th><th>제품타입</th><th class="num">수량</th><th class="num">브라켓</th><th class="num">피스</th><th>출고메모</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`;
    }).join('');
    const period = multiDay ? `${data.from} ~ ${data.to}` : dayjs(data.from).format('YYYY-MM-DD (dd)');
    w.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>배송요약 ${esc(period)}</title>
<style>
  @page { size: A4 portrait; margin: 10mm 8mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif; margin: 0; font-size: 9pt; color: #111; }
  h1 { font-size: 14pt; margin: 0 0 3mm; }
  section { margin-bottom: 5mm; }
  h2 { font-size: 12pt; margin: 0 0 1.5mm; padding: 1mm 2mm; background: #e8e8e8; border-left: 3px solid #111; break-after: avoid; page-break-after: avoid; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  h2 span { font-size: 9pt; font-weight: 400; color: #333; margin-left: 2mm; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  thead th { font-size: 8pt; text-align: left; border-bottom: 1.5px solid #111; padding: 1mm; }
  td { padding: 1.2mm 1mm; border-bottom: 1px solid #ccc; vertical-align: top; word-break: break-all; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  td small { color: #555; font-size: 7.5pt; }
  td .sub { font-size: 8pt; color: #222; margin-top: 1px; }
  .types .acc { margin-top: 1px; padding-left: 6px; border-left: 2px solid #999; }
  .no { color: #666; }
  .num { text-align: right; white-space: nowrap; }
  .types { font-size: 8pt; color: #333; }
  .memo { color: #b00; font-size: 8pt; }
  .memo div + div { margin-top: 1mm; padding-top: 1mm; border-top: 1px dashed #e0a0a0; }
  .foot { margin-top: 2mm; font-size: 7.5pt; color: #666; }
</style></head><body>
<h1>배송요약 ${esc(period)}${q.trim() ? ` <small style="font-size:9pt;font-weight:400">검색: ${esc(q.trim())}</small>` : ''}</h1>
${sections}
<div class="foot">출력 ${dayjs().format('YYYY-MM-DD HH:mm')} · 수량 = 조작 좌+우 · '기타 비용' 제외 · 브라켓/피스 = 비고의 ㄱ/ㅍ 주문 건수</div>
<script>window.onload = function () { window.print(); };</script></body></html>`);
    w.document.close();
  };

  const resultCounts = useMemo(() => {
    const c = {};
    items.forEach((it) => { if (notCounted(it)) return; const r = it.match?.result || 'NO_PORTAL'; c[r] = (c[r] || 0) + 1; });
    return c;
  }, [items]);

  const shownUnresolved = resultFilter === 'ALL' ? unresolved : unresolved.filter((p) => p.match?.result === resultFilter);

  return (
    <ConfigProvider locale={ko_KR}>
      <div className="common-container ds-page">
        <h1>배송요약</h1>

        <Space wrap style={{ marginBottom: 16 }}>
          <RangePicker
            value={range}
            onChange={(v) => setRange(v)}
            allowClear={false}
            format="YYYY-MM-DD"
            inputReadOnly={isMobile}
            disabledDate={(d) => d && d.isAfter(dayjs().add(30, 'day'))}
            presets={[
              { label: '오늘', value: [dayjs(), dayjs()] },
              { label: '어제', value: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
              { label: '이번 주', value: [dayjs().subtract((dayjs().day() + 6) % 7, 'day'), dayjs()] },
              { label: '최근 7일', value: [dayjs().subtract(6, 'day'), dayjs()] },
              { label: '이번 달', value: [dayjs().startOf('month'), dayjs()] },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={fetchData}>조회</Button>
          <Button icon={<DownloadOutlined />} disabled={!data} onClick={handleExcel}>엑셀 다운로드</Button>
          <Button icon={<PrinterOutlined />} disabled={!data} onClick={handlePrint}>A4 출력</Button>
        </Space>

        {data && !data.portal.ok && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="데이터 포털에서 제품 분류를 받지 못해 제품타입을 붙이지 못했습니다." description={data.portal.message} />
        )}
        {data?.portal.ok && !data.portal.goods?.count && (
          <Alert
            type="info" showIcon style={{ marginBottom: 16 }}
            message="포털 기초품목(제품명+원단코드) 목록이 비어 있어 제품 분류 이름으로만 제품타입을 찾았습니다."
            description={data.portal.goods?.error || '기초품목이 채워지면 원단코드까지 맞춰 찾습니다 (예: 롤 안의 가공비). 오래 비어 있으면 포털 관리자에게 알려 주세요.'}
          />
        )}
        {ebmsMismatch.length > 0 && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 16 }}
            message="EBMS 화면의 수량 합계와 다른 날짜가 있습니다."
            description={ebmsMismatch.map((d) => `${d.date}: 가져온 ${fmtQty(d.qty)} / EBMS ${fmtQty(d.ebms_total)}`).join(', ')}
          />
        )}

        {data && (
          <>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={12} md={6}><Card size="small"><Statistic title="거래처" value={new Set(customers.map((c) => `${c.customer}\u0000${c.customer_kind}`)).size} suffix="곳" /></Card></Col>
              <Col xs={12} md={6}><Card size="small"><Statistic title="수량 합계" value={totalQty} formatter={(v) => fmtQty(v)} /></Card></Col>
              <Col xs={12} md={6}><Card size="small"><Statistic title="출고 줄" value={items.reduce((a, it) => a + (notCounted(it) ? 0 : it.lines), 0)} suffix="줄" /></Card></Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="미지정·미매칭 제품"
                    value={unresolved.length}
                    suffix="개"
                    valueStyle={unresolved.length ? { color: '#d48806' } : undefined}
                  />
                </Card>
              </Col>
            </Row>

            <Card size="small" style={{ marginBottom: 12 }} className="ds-ships">
              {['SAME', 'NEXT', 'OTHER'].map((g) => {
                const list = (data.ships || []).filter((x) => x.group === g);
                if (!list.length) return null;
                const dayLabel = g === 'NEXT'
                  ? (multiDay ? '출고일 D+1' : dayjs(data.from).add(1, 'day').format('M/D (dd)'))
                  : (multiDay ? '출고일 D' : dayjs(data.from).format('M/D (dd)'));
                return (
                  <div key={g} className="ds-ships-row">
                    <Text strong style={{ minWidth: 104 }}>{GROUP_LABEL[g]} <Text type="secondary" style={{ fontSize: 12 }}>{g === 'OTHER' ? '' : dayLabel}</Text></Text>
                    <Space size={[4, 6]} wrap>
                      {list.map((x) => (
                        <Tag.CheckableTag
                          key={x.ship}
                          checked={shipSel.includes(x.ship)}
                          onChange={(on) => setShipSel((cur) => (on ? [...cur, x.ship] : cur.filter((v) => v !== x.ship)))}
                          className="ds-ship-chip"
                        >
                          {x.ship} <span className="ds-ship-n">{x.orders}건</span>
                        </Tag.CheckableTag>
                      ))}
                    </Space>
                  </div>
                );
              })}
              <Space size={4} wrap style={{ marginTop: 6 }}>
                <Button size="small" onClick={() => setShipSel((data.ships || []).filter((x) => x.group !== 'OTHER').map((x) => x.ship))}>당일+다음날</Button>
                <Button size="small" onClick={() => setShipSel((data.ships || []).filter((x) => x.group === 'SAME').map((x) => x.ship))}>당일만</Button>
                <Button size="small" onClick={() => setShipSel((data.ships || []).filter((x) => x.group === 'NEXT').map((x) => x.ship))}>다음날만</Button>
                <Button size="small" onClick={() => setShipSel((data.ships || []).map((x) => x.ship))}>모두</Button>
              </Space>
            </Card>

            <Input.Search
              allowClear placeholder="거래처·제품명 검색" value={q} onChange={(e) => setQ(e.target.value)}
              style={{ maxWidth: 320, marginBottom: 12 }}
            />

            <Tabs
              items={[
                {
                  key: 'customer',
                  label: `거래처별 (${customers.length})`,
                  children: (
                    <>
                    <Segmented
                      size="small" style={{ marginBottom: 8 }} value={splitShip ? 'split' : 'merge'}
                      onChange={(v) => setSplitShip(v === 'split')}
                      options={[{ label: '거래처로 합쳐 보기', value: 'merge' }, { label: '배송구분별로 나눠 보기', value: 'split' }]}
                    />
                    <Table
                      rowKey="key" size="small" loading={loading} dataSource={customers} columns={customerColumns}
                      pagination={{ pageSize: 50, showSizeChanger: true, hideOnSinglePage: true }}
                      scroll={isMobile ? undefined : { x: 'max-content' }}
                      tableLayout={isMobile ? 'fixed' : undefined}
                      expandable={{
                        expandedRowRender: (c) => (
                          <Table rowKey={(r) => `${r.ship}|${r.product}`} size="small" pagination={false} dataSource={c.products} columns={productColumns} className="ds-inner" tableLayout={isMobile ? 'fixed' : undefined} />
                        ),
                      }}
                    />
                    </>
                  ),
                },
                {
                  key: 'type',
                  label: `제품타입별 (${byType.length})`,
                  children: (
                    <Table
                      rowKey="key" size="small" loading={loading} dataSource={byType} columns={typeColumns} pagination={false}
                      expandable={{
                        expandedRowRender: (t) => (
                          <Table rowKey="key" size="small" dataSource={t.products} columns={typeProductColumns} pagination={{ pageSize: 20, hideOnSinglePage: true }} />
                        ),
                      }}
                    />
                  ),
                },
                {
                  key: 'unresolved',
                  label: `미지정·미매칭 (${unresolved.length})`,
                  children: (
                    <>
                      <Space wrap style={{ marginBottom: 8 }}>
                        <Segmented
                          size="small" value={resultFilter} onChange={setResultFilter}
                          options={[
                            { label: '전체', value: 'ALL' },
                            ...['UNSET', 'UNMATCHED', 'AMBIGUOUS'].filter((r) => resultCounts[r]).map((r) => ({ label: `${RESULT_META[r].label} ${resultCounts[r]}줄`, value: r })),
                          ]}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>제품타입은 영업 웹 제품 분류 화면(데이터 포털)에서 고릅니다.</Text>
                      </Space>
                      <Table rowKey="key" size="small" dataSource={shownUnresolved} columns={unresolvedColumns} pagination={{ pageSize: 30, hideOnSinglePage: true }} scroll={{ x: 'max-content' }} />
                    </>
                  ),
                },
                {
                  key: 'days',
                  label: '날짜별',
                  children: (
                    <Table
                      rowKey="date" size="small" dataSource={data.days} pagination={false}
                      columns={[
                        { title: '출고일', dataIndex: 'date', key: 'date', render: (v) => dayjs(v).format('YYYY-MM-DD (dd)') },
                        { title: '줄', dataIndex: 'lines', key: 'lines', align: 'right' },
                        { title: '수량 (기타 비용 포함)', dataIndex: 'qty', key: 'qty', align: 'right', render: fmtQty },
                        { title: 'EBMS 표시 합계', dataIndex: 'ebms_total', key: 'e', align: 'right', render: fmtQty },
                      ]}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </div>
    </ConfigProvider>
  );
};

export default DeliverySummaryPage;
